import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { ROUTES } from '../constants/routes';
import {
  createPaymentLink,
  createPaymentOrder,
  getUserSubscriptions,
  verifyPayment,
} from '../services/payments';
import { openRazorpayCheckout } from '../utils/razorpay';
import { RAZORPAY_KEY_ID } from '../config/env';
import {
  clearPendingPayment,
  readPendingPayment,
  savePendingPayment,
} from '../utils/pendingPayment';

const POLL_MS = 2500;
const MAX_WAIT_MS = 12 * 60 * 1000;

function resolvePlanId(plan) {
  const id = Number(plan?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function resolveRazorpayKey(orderData) {
  return (
    RAZORPAY_KEY_ID ||
    null
  );
}

function resolveAmountInPaise(orderData) {
  const direct = Number(orderData?.amount_in_paise);
  if (Number.isFinite(direct) && direct > 0) return Math.round(direct);

  const amount = Number(orderData?.amount);
  if (Number.isFinite(amount) && amount > 0) return Math.round(amount * 100);

  return null;
}

function buildCallbackUrl() {
  return `${window.location.origin}${ROUTES.PAYMENT_CALLBACK}`;
}

function buildSuccessRedirectUrl() {
  return `${window.location.origin}${ROUTES.PAYMENT_SUCCESS}`;
}

function isFullyActiveSubscription(sub, subscriptionId) {
  if (!sub || String(sub.id) !== String(subscriptionId)) return false;
  return Boolean(
    sub.active === true && (sub.status === 'completed' || sub.is_subscription === true)
  );
}

function isPaymentMarkedCompleted(sub, subscriptionId) {
  if (!sub || String(sub.id) !== String(subscriptionId)) return false;
  const status = String(sub.status || '').toLowerCase();
  return status === 'completed' || status === 'paid';
}

export function usePlanPayment({ onSuccess } = {}) {
  const { isLoggedIn, openAuthPopup } = useAuthContext();
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); 
  const [paymentMeta, setPaymentMeta] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);

  const pollTimerRef = useRef(null);
  const startedAtRef = useRef(0);
  const handledRef = useRef(false);

  const clearPaymentFeedback = useCallback(() => {
    setPaymentError(null);
    setPaymentSuccess(null);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const closePaymentStatus = useCallback(() => {
    stopPolling();
    handledRef.current = false;
    setPaymentStatus(null);
    setPaymentMeta(null);
    setStatusMessage('');
    setCheckingStatus(false);
    clearPendingPayment();
  }, [stopPolling]);

  const showSuccess = useCallback(
    (meta = {}) => {
      if (handledRef.current) return;
      handledRef.current = true;
      stopPolling();
      setPaymentMeta((prev) => ({ ...(prev || {}), ...meta }));
      setStatusMessage('Your subscription is active. You can start learning right away.');
      setPaymentStatus('success');
      setPaymentSuccess('Payment successful. Your plan is now active.');
      onSuccess?.();
    },
    [onSuccess, stopPolling]
  );

  const showFailed = useCallback(
    (message, meta = {}) => {
      if (handledRef.current) return;
      handledRef.current = true;
      stopPolling();
      setPaymentMeta((prev) => ({ ...(prev || {}), ...meta }));
      setStatusMessage(message || 'Payment failed. Please try again.');
      setPaymentStatus('failed');
      setPaymentError(message || 'Payment failed. Please try again.');
    },
    [stopPolling]
  );

  const checkPaymentStatus = useCallback(async () => {
    const current = readPendingPayment() || paymentMeta;
    const subscriptionId = current?.subscriptionId;
    if (!subscriptionId) return false;

    setCheckingStatus(true);
    try {
      const res = await getUserSubscriptions();
      const subscriptions = res?.data?.subscriptions || [];
      const match = subscriptions.find((sub) => String(sub.id) === String(subscriptionId));

      if (match && isFullyActiveSubscription(match, subscriptionId)) {
        showSuccess(current);
        return true;
      }

      if (match && isPaymentMarkedCompleted(match, subscriptionId)) {
        setStatusMessage(
          'Payment received, but subscription is not active yet. Waiting for verification…'
        );
        return false;
      }

      setStatusMessage('Payment not confirmed yet. Complete payment in the Razorpay window.');
      return false;
    } catch {
      setStatusMessage('Could not verify payment yet. Retrying…');
      return false;
    } finally {
      setCheckingStatus(false);
    }
  }, [paymentMeta, showSuccess]);

  const startPolling = useCallback(() => {
    stopPolling();
    handledRef.current = false;
    startedAtRef.current = Date.now();

    pollTimerRef.current = window.setInterval(async () => {
      if (handledRef.current) return;
      if (Date.now() - startedAtRef.current > MAX_WAIT_MS) {
        stopPolling();
        setStatusMessage('Still waiting. If you already paid, tap “I’ve paid”.');
        return;
      }
      await checkPaymentStatus();
    }, POLL_MS);

    checkPaymentStatus();
  }, [checkPaymentStatus, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const openPaymentWindow = useCallback(() => {
    const current = readPendingPayment() || paymentMeta;
    if (!current?.paymentUrl) return;
    const popup = window.open(current.paymentUrl, 'chse_razorpay_payment');
    const next = { ...current, paymentWindowOpened: Boolean(popup) };
    savePendingPayment(next);
    setPaymentMeta(next);
    if (!popup) {
      setStatusMessage('Popup blocked. Allow popups, or open the payment link below.');
    }
  }, [paymentMeta]);

  const startCheckoutPayment = useCallback(
    async (plan, orderRes) => {
      const orderData = orderRes?.data || {};
      const key = resolveRazorpayKey(orderData);
      const orderId = orderData.order_id;
      const subscriptionId = orderData.subscription_id;
      const amountInPaise = resolveAmountInPaise(orderData);

      if (!key || !orderId || !subscriptionId || !amountInPaise) {
        return false;
      }

      const meta = {
        planName: plan?.name || orderData.plan?.name || 'Subscription',
        amount: orderData.amount,
        subscriptionId,
        orderId,
      };
      savePendingPayment(meta);
      setPaymentMeta(meta);
      setStatusMessage('Complete payment in the Razorpay checkout…');
      setPaymentStatus('pending');
      handledRef.current = false;

      try {
        const checkoutResult = await openRazorpayCheckout({
          key,
          orderId,
          amountInPaise,
          currency: orderData.currency || 'INR',
          description: plan?.name || orderData.plan?.name || 'CHSE Express subscription',
          onSuccess: async (response) => {
            const verifyRes = await verifyPayment({
              subscriptionId,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            if (verifyRes?.success === false) {
              throw new Error(verifyRes?.message || 'Payment verification failed');
            }

            showSuccess({
              ...meta,
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
            });
          },
        });

        if (checkoutResult?.dismissed) {
          showFailed('Payment was cancelled before completion.', meta);
        }

        return true;
      } catch (error) {
        showFailed(error?.message || 'Payment failed. Please try again.', meta);
        return true;
      }
    },
    [showFailed, showSuccess]
  );

  const startPaymentLink = useCallback(
    async (plan, options) => {
      const planId = resolvePlanId(plan);
      const linkRes = await createPaymentLink({
        planId,
        couponCode: options.couponCode || null,
        callbackUrl: buildCallbackUrl(),
        redirectUrl: buildSuccessRedirectUrl(),
      });

      if (linkRes?.success === false) {
        throw new Error(linkRes?.message || 'Unable to create payment link');
      }

      const paymentUrl = linkRes?.data?.payment_link_url;
      const subscriptionId = linkRes?.data?.subscription_id;
      const orderId =
        linkRes?.data?.order_id || linkRes?.data?.razorpay_order_id || null;

      if (!paymentUrl) {
        throw new Error('Payment link not available. Please try again.');
      }
      if (!subscriptionId) {
        throw new Error('Subscription id missing. Please try again.');
      }

      const popup = window.open(paymentUrl, 'chse_razorpay_payment');
      const meta = {
        planName: plan?.name || linkRes?.data?.plan?.name || 'Subscription',
        amount: linkRes?.data?.amount,
        subscriptionId,
        orderId,
        paymentUrl,
        paymentWindowOpened: Boolean(popup),
      };

      savePendingPayment(meta);
      setPaymentMeta(meta);
      setStatusMessage(
        popup
          ? 'Complete payment in the Razorpay window. This page will update automatically.'
          : 'Popup blocked. Allow popups, or open the payment link below.'
      );
      setPaymentStatus('pending');
      startPolling();
      return true;
    },
    [startPolling]
  );

  const startPayment = useCallback(
    async (plan, options = {}) => {
      if (!isLoggedIn) {
        openAuthPopup();
        return false;
      }

      const planId = resolvePlanId(plan);
      if (!planId) {
        setPaymentError('Unable to start payment for this plan. Please refresh and try again.');
        return false;
      }

      setPayingPlanId(planId);
      setPaymentError(null);
      setPaymentSuccess(null);
      handledRef.current = false;

      try {
        try {
          const orderRes = await createPaymentOrder({
            planId,
            couponCode: options.couponCode || null,
          });

          if (orderRes?.success !== false) {
            const handled = await startCheckoutPayment(plan, orderRes);
            if (handled) return true;
          }
        } catch {
          // Fall back to payment link when create_order/checkout is unavailable.
        }

        return await startPaymentLink(plan, options);
      } catch (error) {
        const message = error?.message || 'Payment failed. Please try again.';
        setPaymentError(message);
        showFailed(message, { planName: plan?.name || 'Subscription' });
        return false;
      } finally {
        setPayingPlanId(null);
      }
    },
    [isLoggedIn, openAuthPopup, showFailed, startCheckoutPayment, startPaymentLink]
  );

  return {
    startPayment,
    payingPlanId,
    paymentError,
    paymentSuccess,
    clearPaymentFeedback,
    paymentStatus,
    paymentMeta,
    statusMessage,
    checkingStatus,
    closePaymentStatus,
    openPaymentWindow,
    checkPaymentStatus,
    isLoggedIn,
    openAuthPopup,
  };
}

export function useFoundationPackCheckout({ plans, startPayment, isPlanSubscribed }) {
  const { isLoggedIn, openAuthPopup } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.id) === String(selectedPlanId)) || plans[0] || null,
    [plans, selectedPlanId]
  );

  const openPack = useCallback(
    (plan) => {
      if (!isLoggedIn) {
        openAuthPopup();
        return;
      }

      if (!plans?.length) return;

      const preferredId = plan?.id ?? plans[0]?.id ?? null;
      setSelectedPlanId(preferredId);
      setOpen(true);
    },
    [isLoggedIn, openAuthPopup, plans]
  );

  const closePack = useCallback(() => {
    setOpen(false);
  }, []);

  const handleProceed = useCallback(
    async ({ plan, couponCode }) => {
      if (isPlanSubscribed?.(plan)) {
        return false;
      }
      const ok = await startPayment(plan, { couponCode });
      if (ok) setOpen(false);
      return ok;
    },
    [isPlanSubscribed, startPayment]
  );

  useEffect(() => {
    if (!open) return;
    if (!plans.some((plan) => String(plan.id) === String(selectedPlanId))) {
      setSelectedPlanId(plans[0]?.id ?? null);
    }
  }, [open, plans, selectedPlanId]);

  return {
    open,
    selectedPlan,
    selectedPlanId,
    setSelectedPlanId,
    openPack,
    closePack,
    handleProceed,
  };
}
